import {addCommasToNumber} from "../../utils";
import {money, money2, count, percent, squareFeet, rateLabel, pricePsf} from "../UnderwritingMultiFamily/model/format";

/**
 * Every Multi-Family page's data, read from the Multi-Family underwriting
 * (UnderwritingMultiFamily, built on MF_BOV_Underwriting_Template.xlsx).
 *
 * `mf` is `{inputs, calc}`: the popup's stored inputs and calculate(inputs). The deck prints
 * the same figures the popup shows - the Marketing tab is the deck's page 02, the Current
 * Rent Roll its page 03 unit mix, and the Pricing Range its page 04 bands.
 */

const DASH = '-';

export function readProperty({inputs}) {
    return {
        name: inputs.name,
        address: inputs.address,
        city: inputs.city,
        state: inputs.state,
        zip: inputs.zip,
    };
}

function lotSize(inputs) {
    const acres = Number(inputs.lot_size);
    return acres ? `${acres.toFixed(2)} Acres` : DASH;
}

// ---------------------------------------------------------------- page 02, left card

/** Marketing!B5:C13 - the Offering Summary rows. */
export function readOfferingSummary({inputs, calc}) {
    const current = calc.valuation.current;
    return {
        name: 'OFFERING SUMMARY',
        rows: [
            {label: 'Price', value: money(current.price)},
            {label: 'Price Per Unit', value: money(current.price_per_unit)},
            {label: 'Price PSF', value: pricePsf(current.psf)},
            {label: 'In Place Cap Rate', value: percent(current.cap_rate)},
            {label: 'Net Operating Income', value: money(current.noi)},
            {label: 'Number of Units', value: count(calc.units)},
            {label: 'Year Built/Renovated', value: inputs.year_built || DASH},
            {label: 'Lot Size', value: lotSize(inputs)},
        ],
    };
}

// --------------------------------------------------------------- page 02, right card

/** Marketing!F5:H23 - Income & Expenses, ANNUAL and PER UNIT, in the sheet's row order. */
export function readIncomeAndExpenses({calc}) {
    const income = calc.income;
    const rates = income.rates;
    const line = (label, figures, extra = {}) => ({
        label,
        annual: money(figures.current),
        perUnit: money(figures.current_per_unit),
        ...extra,
    });

    return {
        header: ['INCOME & EXPENSES', 'ANNUAL', 'PER UNIT'],
        rows: [
            line('Gross Potential Rent', income.gross_potential_rent),
            line(`Less: Physical Vacancy (${rateLabel(rates.vacancy.current)})`, income.vacancy),
            line(`Less: Economic Vacancy (${rateLabel(rates.economic_vacancy.current)})`, income.economic_vacancy),
            line('Other Income - RUBS, Fees, Parking', income.other_income),
            line('Effective Gross Income', income.effective_gross_income, {strong: true}),
            {label: 'Operating Expenses', heading: true},
            // Marketing-sheet order, as EXPENSE_LINES lists them.
            ...income.expenses.map(e => line(
                e.key === 'management_fee' ? `${e.label} (${rateLabel(rates.management_fee.current)})` : e.label, e)),
            line('TOTAL EXPENSES', income.total_expenses, {total: true}),
        ],
        footer: line('Net Operating Income', income.net_operating_income),
    };
}

// ------------------------------------------------------------------------- page 03

function mixRow(row) {
    return {
        plan: row.floor_plan || DASH,
        units: count(row.units),
        avgSf: count(row.avg_sf),
        rent: money(row.rent),
        rentSf: money2(row.rent_sf),
        proformaRent: money(row.proforma_rent),
        proformaRentSf: money2(row.proforma_rent_sf),
    };
}

/** Marketing!B16:C21 (Asset Overview) and the Current Rent Roll unit mix. */
export function readUnitSummary({calc}) {
    const roll = calc.rentRoll;
    const parking = calc.propertyInfo.parking_per_unit;
    return {
        overview: [
            {label: 'Total Units', value: count(calc.units)},
            {label: 'Avg. Unit Size', value: squareFeet(roll.total.avg_sf)},
            {label: 'Occupancy', value: percent(roll.occupancy, 0)},
            {label: 'Parking', value: parking ? `${parking.toFixed(2)}/Unit` : DASH},
            {label: 'Rentable SF', value: calc.rentableSf ? `${addCommasToNumber(Math.round(calc.rentableSf))} RSF` : DASH},
        ],
        unitMix: roll.rows.filter(row => row.floor_plan || row.units).map(mixRow),
        unitMixTotal: mixRow(roll.total),
    };
}

// ------------------------------------------------------------------------- page 04

/**
 * The three price bands. The Pricing Range runs from one BPS step below the cap rate to
 * three above; the deck takes the step below (aggressive), the cap rate itself (anticipated)
 * and the step above (conservative).
 */
export function readPricing({calc}) {
    const band = step => {
        const column = calc.pricingRange.find(c => c.step === step) || {};
        return {
            price: money(column.price),
            psf: money2(column.psf),
            capRate: percent(column.cap_rate),
        };
    };
    return {
        aggressive: band(-1),
        anticipated: band(0),
        conservative: band(1),
    };
}

/** Page 04's facts: Year Built, NOI, Price Per Unit and Number of Units. */
export function readFacts({inputs, calc}) {
    const current = calc.valuation.current;
    return [
        {value: inputs.year_built || DASH, label: 'YEAR BUILT'},
        {value: money(current.noi), label: 'NET OPERATING INCOME'},
        {value: money(current.price_per_unit), label: 'PRICE PER UNIT'},
        {value: count(calc.units), label: 'NUMBER OF UNITS'},
    ];
}
